import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { Sparkles, Brain, X } from "lucide-react";
import { useBranding } from "../../context/BrandingContext";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "hi", label: "हिन्दी" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "ru", label: "Русский" },
  { code: "tr", label: "Türkçe" },
  { code: "pl", label: "Polski" },
];

export default function VoiceStartModal({ onClose, onStart, selectedModelName, hasModel }) {
  const { t } = useTranslation();
  const branding = useBranding();
  const appName = branding.app_name || "Faro";
  const [mode, setMode] = useState("default"); // 'default' | 'thinking'
  const [language, setLanguage] = useState("en");
  const [record, setRecord] = useState(true);

  useEffect(() => {
    api.get("/voice-settings").then(({ data }) => {
      if (data?.default_language) setLanguage(data.default_language);
    }).catch(() => {});
  }, []);

  const start = () => {
    onStart({ mode, language, record });
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md grid place-items-center p-4" data-testid="voice-start-modal">
      <div className="w-full max-w-lg border border-border bg-card">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ {t("voice:modal.eyebrow")} ]</div>
            <div className="font-display font-bold tracking-tight text-xl mt-1">
              {t("voice:modal.title", { app: appName })}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="voice-start-close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">{t("voice:mode.label")}</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 space-y-2">
              <label
                className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition ${
                  mode === "default" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                }`}
                data-testid="voice-mode-default"
              >
                <RadioGroupItem value="default" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <div className="font-semibold">{t("voice:mode.realtime.title", { app: appName })}</div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">
                    {t("voice:mode.realtime.body")}
                  </p>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition ${
                  mode === "thinking" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                } ${!hasModel ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="voice-mode-thinking"
              >
                <RadioGroupItem value="thinking" className="mt-1" disabled={!hasModel} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-primary" />
                    <div className="font-semibold">
                      {t("voice:mode.thinking.title")}
                      {selectedModelName && (
                        <span className="font-mono text-xs text-muted-foreground ml-1.5">({selectedModelName})</span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">
                    {t("voice:mode.thinking.body")}
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {mode === "thinking" && (
            <div
              className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 p-3 rounded-sm text-xs leading-relaxed"
              data-testid="thinking-mode-warning"
            >
              {t("voice:mode.thinking.warning")}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">{t("voice:language.label")}</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="voice-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>{l.label} <span className="text-muted-foreground font-mono ml-1.5">({l.code})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between border border-border p-3 rounded-sm">
            <div>
              <div className="text-sm font-medium">{t("voice:record.label")}</div>
              <div className="text-xs text-muted-foreground">
                {t("voice:record.help")}
              </div>
            </div>
            <Switch checked={record} onCheckedChange={setRecord} data-testid="voice-record-toggle" />
          </div>
        </div>

        <div className="p-5 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="voice-start-cancel">{t("voice:cancel")}</Button>
          <Button onClick={start} className="rounded-sm" data-testid="voice-start-confirm">{t("voice:start")}</Button>
        </div>
      </div>
    </div>
  );
}
